// Coalesce ResizeObserver callbacks into requestAnimationFrame.
//
// Browsers emit a benign "ResizeObserver loop completed with undelivered
// notifications" (a.k.a. "loop limit exceeded") warning when an observer
// callback causes a layout change that can't be flushed in the same frame.
// It's harmless, but Create React App's dev error overlay surfaces it as an
// "Uncaught runtime error", interrupting development — most visibly when the
// auto-growing multiline inputs (e.g. StackComposer) reflow on viewport resize.
//
// Deferring each callback to the next animation frame breaks the synchronous
// loop, so the notification is always delivered and the warning never fires.
// The one-frame delay is imperceptible and matches what several UI libraries do
// internally. Applied for all ResizeObserver instances, dev and prod alike.
//
// The one-frame defer has a sharp edge: if the observed element unmounts during
// that frame (e.g. navigating away from a page with MUI's auto-growing
// TextareaAutosize), the already-queued callback still fires afterwards and the
// consumer calls getComputedStyle on the now-detached/null node, throwing
// "Failed to execute 'getComputedStyle' on 'Window': parameter 1 is not of type
// 'Element'." We guard against that by (a) cancelling the pending frame when the
// observer is disconnected (MUI disconnects on unmount), and (b) dropping the
// notification if every observed target has left the DOM.
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;

  window.ResizeObserver = class ResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      let frameId = 0;
      super((entries, observer) => {
        // Only the latest resize in a frame needs delivering.
        if (frameId) {
          window.cancelAnimationFrame(frameId);
        }
        frameId = window.requestAnimationFrame(() => {
          frameId = 0;
          // Skip if every observed element has detached (post-unmount) — otherwise
          // consumers like TextareaAutosize call getComputedStyle on a null node.
          if (entries.some((entry) => entry.target && entry.target.isConnected)) {
            callback(entries, observer);
          }
        });
      });
      this._cancelPendingFrame = () => {
        if (frameId) {
          window.cancelAnimationFrame(frameId);
          frameId = 0;
        }
      };
    }

    disconnect() {
      this._cancelPendingFrame();
      super.disconnect();
    }
  };
}
